/* ------------------------------------------------------------------------------------------------------------------
   MOWOW — Product Quick Nav (Impact)
   What this does (Dawn logic, adapted for Impact):
   - The bar is rendered as a normal section under Product Info.
   - When you scroll down and the bar reaches the sticky header area, it becomes FIXED at the top (covers the header).
   - When you scroll up near its original position, it returns back into the normal flow (header becomes visible again).
   - Smooth-scroll to anchors with correct offset.
   - Active link highlight while scrolling.
   - Option: hide links whose anchor targets don't exist.

   Why NOT `position: sticky`:
   - In Shopify themes, a standalone section containing only the bar has a very short parent height.
     `position: sticky` cannot remain sticky after its parent ends. Fixed + placeholder avoids that.
------------------------------------------------------------------------------------------------------------------ */

(function () {
  const NAV_SELECTOR = '[data-mowow-quick-nav]';
  const LINK_SELECTOR = '[data-mowow-quick-nav-link]';

  const GAP_PX = 12;     // breathing room below the fixed bar
  const FIX_BUFFER = 10; // fix a bit earlier
  const UNFIX_BUFFER = 50;

  function asPx(value) {
    const n = parseFloat(String(value || '').replace('px', ''));
    return Number.isFinite(n) ? n : 0;
  }

  function debounce(fn, delay) {
    let t;
    return function (...args) {
      window.clearTimeout(t);
      t = window.setTimeout(() => fn.apply(this, args), delay);
    };
  }

  function getStickyAreaHeight() {
    const rootStyle = getComputedStyle(document.documentElement);
    const stickyArea = rootStyle.getPropertyValue('--sticky-area-height');
    const header = rootStyle.getPropertyValue('--header-height');
    return asPx(stickyArea) || asPx(header) || 0;
  }

  function getNavHeight(nav) {
    return Math.round(nav.getBoundingClientRect().height || 0);
  }

  function ensurePlaceholder(nav) {
    const prev = nav.previousElementSibling;
    if (prev && prev.classList && prev.classList.contains('mowow-quick-nav__placeholder')) return prev;

    const ph = document.createElement('div');
    ph.className = 'mowow-quick-nav__placeholder';
    ph.setAttribute('aria-hidden', 'true');
    ph.style.height = '0px';

    nav.parentNode.insertBefore(ph, nav);
    return ph;
  }

  function setRootVars(nav, isFixed) {
    const navH = getNavHeight(nav);
    const stickyH = getStickyAreaHeight();

    // Expose for CSS scroll-margin-top etc.
    document.documentElement.style.setProperty('--mowow-quick-nav-height', `${navH}px`);
    document.documentElement.style.setProperty('--mowow-quick-nav-offset-stuck', `${navH + GAP_PX}px`);
    document.documentElement.style.setProperty('--mowow-quick-nav-offset-unstuck', `${stickyH + GAP_PX}px`);
    document.documentElement.style.setProperty('--mowow-quick-nav-offset', `${(isFixed ? (navH + GAP_PX) : (stickyH + GAP_PX))}px`);
  }

  function initOne(nav) {
    if (!nav || nav.dataset.mowowInit === '1') return;
    nav.dataset.mowowInit = '1';

    const hideMissing = nav.dataset.hideMissingTargets === 'true';
    const placeholder = ensurePlaceholder(nav);

    // Resolve targets + optionally remove links that don't exist
    const links = Array.from(nav.querySelectorAll(LINK_SELECTOR));
    const items = [];

    links.forEach((link) => {
      const href = link.getAttribute('href') || '';
      const id = href.startsWith('#') ? href.slice(1) : '';
      const target = id ? document.getElementById(id) : null;

      if (!target && hideMissing) {
        link.remove();
        return;
      }
      items.push({ link, id, target });
    });

    if (!items.length) return;
    const candidates = items.filter((it) => it.target && it.id);
    const candidatesByDocument = [...candidates].sort((a, b) => {
      if (a.target === b.target) return 0;
      const pos = a.target.compareDocumentPosition(b.target);
      if (pos & Node.DOCUMENT_POSITION_FOLLOWING) return -1;
      if (pos & Node.DOCUMENT_POSITION_PRECEDING) return 1;
      return 0;
    });

    let isFixed = false;
    let lastScrollTop = window.pageYOffset || document.documentElement.scrollTop;

    // Use placeholder position as the stable reference for thresholds
    let originalTop = 0;

    function setActiveLinkById(activeId, keepVisible = false) {
      let activeLink = null;

      items.forEach((it) => {
        const isActive = !!activeId && it.id === activeId;
        it.link.classList.toggle('is-active', isActive);
        if (isActive) activeLink = it.link;
      });

      if (keepVisible && activeLink && typeof activeLink.scrollIntoView === 'function') {
        activeLink.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
      }
    }

    function getCurrentTopOffset() {
      const navH = getNavHeight(nav);
      const stickyH = getStickyAreaHeight();
      return (isFixed ? (navH + GAP_PX) : (stickyH + GAP_PX));
    }

    function updateActiveFromScrollFallback() {
      if (!candidatesByDocument.length) return;

      const topLine = getCurrentTopOffset() + 2;
      let current = candidatesByDocument[0];

      for (const candidate of candidatesByDocument) {
        const top = candidate.target.getBoundingClientRect().top;
        if (top - topLine <= 0) {
          current = candidate;
        } else {
          break;
        }
      }

      setActiveLinkById(current.id);
    }

    function recalcOriginalTop() {
      const y = window.pageYOffset || document.documentElement.scrollTop;
      originalTop = placeholder.getBoundingClientRect().top + y;
    }

    function setFixed(next) {
      if (next === isFixed) return;

      if (next) {
        // set placeholder height BEFORE fixing to prevent jump
        placeholder.style.height = `${getNavHeight(nav)}px`;
        nav.classList.add('is-stuck');
        isFixed = true;
      } else {
        // unfix first (nav returns in flow), then remove placeholder height
        nav.classList.remove('is-stuck');
        isFixed = false;
        placeholder.style.height = '0px';
      }

      setRootVars(nav, isFixed);
      rebuildActiveObserver();
    }

    function updateFixedState() {
      const y = window.pageYOffset || document.documentElement.scrollTop;
      const direction = y > lastScrollTop ? 'down' : 'up';
      lastScrollTop = y;

      const stickyH = getStickyAreaHeight();

      // Fix when the bar reaches the sticky header area (bottom of header)
      const fixThreshold = originalTop - stickyH - FIX_BUFFER;
      const unfixThreshold = originalTop - stickyH - UNFIX_BUFFER;

      if (!isFixed) {
        if (y >= fixThreshold) setFixed(true);
      } else {
        // Unfix on scroll-up when we are close to its original position again
        if (direction === 'up' && y <= unfixThreshold) {
          setFixed(false);
          return;
        }
        // safety: top of page
        if (y <= 50) setFixed(false);
      }
    }

    // Smooth scroll with correct offset
    function smoothScrollTo(id) {
      if (!id) return;
      const target = document.getElementById(id);
      if (!target) return;

      const y = window.pageYOffset || document.documentElement.scrollTop;
      const targetTop = target.getBoundingClientRect().top + y;

      const navH = getNavHeight(nav);
      const top = Math.max(0, targetTop - navH - GAP_PX);

      window.scrollTo({ top, behavior: 'smooth' });

      try { history.replaceState(null, '', `#${id}`); } catch (err) {}
    }

    nav.addEventListener('click', (e) => {
      const el = e.target instanceof Element ? e.target.closest('a[href^="#"]') : null;
      if (!el) return;

      const href = el.getAttribute('href') || '';
      const id = href.startsWith('#') ? href.slice(1) : '';
      if (!id) return;

      const target = document.getElementById(id);
      if (!target) return;

      e.preventDefault();
      setActiveLinkById(id, true);
      smoothScrollTo(id);
    });

    // Active state tracking
    let activeObserver = null;

    function rebuildActiveObserver() {
      if (!('IntersectionObserver' in window)) return;

      if (activeObserver) {
        activeObserver.disconnect();
        activeObserver = null;
      }

      if (!candidatesByDocument.length) return;

      const navH = getNavHeight(nav);
      const stickyH = getStickyAreaHeight();
      const topOffset = (isFixed ? (navH + GAP_PX) : (stickyH + GAP_PX));

      activeObserver = new IntersectionObserver(
        (entries) => {
          const visible = (entries || [])
            .filter((en) => en.isIntersecting)
            .sort((a, b) => Math.abs(a.boundingClientRect.top) - Math.abs(b.boundingClientRect.top))[0];

          if (!visible || !visible.target) return;

          const activeId = visible.target.id;
          setActiveLinkById(activeId);
        },
        {
          root: null,
          threshold: [0.1, 0.25, 0.5],
          rootMargin: `-${topOffset}px 0px -70% 0px`,
        }
      );

      candidatesByDocument.forEach((it) => activeObserver.observe(it.target));
    }

    // Init
    recalcOriginalTop();
    setRootVars(nav, isFixed);
    rebuildActiveObserver();
    updateFixedState();
    updateActiveFromScrollFallback();

    // Scroll/resize listeners
    const onScroll = debounce(() => {
      updateFixedState();
      updateActiveFromScrollFallback();
    }, 20);
    window.addEventListener('scroll', onScroll, { passive: true });

    window.addEventListener(
      'resize',
      debounce(() => {
        recalcOriginalTop();
        if (isFixed) placeholder.style.height = `${getNavHeight(nav)}px`;
        setRootVars(nav, isFixed);
        rebuildActiveObserver();
        updateFixedState();
        updateActiveFromScrollFallback();
      }, 120),
      { passive: true }
    );
  }

  function init(root) {
    (root || document).querySelectorAll(NAV_SELECTOR).forEach(initOne);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document));
  } else {
    init(document);
  }

  // Theme editor support
  document.addEventListener('shopify:section:load', (e) => init(e.target || document));
})();
